import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useI18n } from '../contexts/I18nContext';
import { fetchIcons, createIcon, uploadIcon, updateIcon, deleteIcon } from '../utils/api';
import './IconPicker.css';

// IconField renders a compact button previewing the current icon (the stored
// renderable HTML) and opens the IconPicker modal to change it. `value` is the
// icon HTML; onChange receives the chosen icon's HTML ('' clears it).
export function IconField({ value, onChange, title }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" className="icon-field" title={title || t('editor.link_icon')}
        onClick={() => setOpen(true)}>
        {value
          ? <span className="icon-field-preview" dangerouslySetInnerHTML={{ __html: value }} />
          : <span className="icon-field-empty">{t('editor.icon_choose')}</span>}
      </button>
      {open && (
        <IconPicker value={value} onClose={() => setOpen(false)}
          onChange={v => { onChange(v); setOpen(false); }} />
      )}
    </>
  );
}

// IconPicker is a modal grid of reusable custom icons (CRUD-backed). Selecting a
// cell returns its renderable HTML value. The currently-selected icon is
// highlighted and floated to the front of the grid.
export default function IconPicker({ value, onChange, onClose }) {
  const { t } = useI18n();
  const [icons, setIcons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [editId, setEditId] = useState(null); // id being edited, or 'new'
  const [fName, setFName] = useState('');
  const [fValue, setFValue] = useState('');
  const [fUrl, setFUrl] = useState('');
  const [fFile, setFFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    (async () => {
      try { setIcons((await fetchIcons()) || []); }
      catch (e) { setErr(e.message || String(e)); }
      finally { setLoading(false); }
    })();
  }, []);

  // Selected icon first; if the current value isn't a saved icon, prepend a
  // read-only "current" cell so it's still shown and highlighted.
  const ordered = useMemo(() => {
    const list = [...icons];
    const idx = list.findIndex(i => i.value === value);
    if (idx > 0) list.unshift(list.splice(idx, 1)[0]);
    if (value && idx === -1) {
      list.unshift({ id: '__current__', name: t('editor.icon_current'), value, _ephemeral: true });
    }
    return list;
  }, [icons, value, t]);

  const resetForm = () => { setEditId(null); setFName(''); setFValue(''); setFUrl(''); setFFile(null); };

  const submit = async () => {
    setBusy(true); setErr('');
    try {
      if (editId === 'new') {
        let created;
        if (fFile) created = await uploadIcon(fFile, fName);
        else created = await createIcon({ name: fName, value: fValue.trim(), url: fUrl.trim() });
        setIcons(prev => [...prev, created]);
      } else {
        const upd = await updateIcon(editId, { name: fName, value: fValue });
        setIcons(prev => prev.map(i => (i.id === editId ? upd : i)));
      }
      resetForm();
    } catch (e) { setErr(e.message || String(e)); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm(t('editor.icon_delete_confirm'))) return;
    setErr('');
    try { await deleteIcon(id); setIcons(prev => prev.filter(i => i.id !== id)); }
    catch (e) { setErr(e.message || String(e)); }
  };

  const startEdit = (ic) => { setEditId(ic.id); setFName(ic.name); setFValue(ic.value); setFUrl(''); setFFile(null); };
  const canSubmit = editId === 'new' ? (!!fFile || fValue.trim() || fUrl.trim()) : fValue.trim() || fName.trim();

  return ReactDOM.createPortal(
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal icon-picker" style={{ maxWidth: 540 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{t('editor.icon_picker_title')}</h2>
          <button className="btn-icon" title={t('common.cancel')} onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          {err && <div className="icon-picker-err">{err}</div>}
          <div className="icon-grid">
            <button type="button" className={`icon-cell icon-cell-none ${!value ? 'sel' : ''}`}
              title={t('editor.icon_none')} onClick={() => onChange('')}>∅</button>
            {ordered.map(ic => (
              <div key={ic.id} className={`icon-cell-wrap ${ic.value === value ? 'sel' : ''}`}>
                <button type="button" className="icon-cell" title={ic.name}
                  onClick={() => onChange(ic.value)}
                  dangerouslySetInnerHTML={{ __html: ic.value }} />
                {!ic._ephemeral && (
                  <span className="icon-cell-actions">
                    <button type="button" title={t('common.edit')} onClick={() => startEdit(ic)}>✎</button>
                    <button type="button" title={t('common.delete')} onClick={() => remove(ic.id)}>✕</button>
                  </span>
                )}
              </div>
            ))}
            {loading && <span className="icon-picker-loading">…</span>}
          </div>

          {editId == null ? (
            <button type="button" className="btn btn-secondary btn-sm icon-add-btn"
              onClick={() => { resetForm(); setEditId('new'); }}>
              {t('editor.icon_add')}
            </button>
          ) : (
            <div className="icon-form">
              <div className="icon-form-title">
                {editId === 'new' ? t('editor.icon_add') : t('editor.icon_edit')}
              </div>
              <input className="ann-img-input" placeholder={t('editor.icon_name_placeholder')}
                value={fName} onChange={e => setFName(e.target.value)} />
              <input className="ann-img-input" placeholder={t('editor.icon_value_placeholder')}
                value={fValue} onChange={e => setFValue(e.target.value)} />
              {editId === 'new' && (
                <>
                  <input className="ann-img-input" type="url" placeholder={t('editor.icon_url_placeholder')}
                    value={fUrl} onChange={e => setFUrl(e.target.value)} />
                  <div className="icon-form-upload">
                    <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }}
                      onChange={e => setFFile(e.target.files?.[0] || null)} />
                    <button type="button" className="btn btn-secondary btn-sm"
                      onClick={() => fileRef.current?.click()}>{t('editor.icon_upload')}</button>
                    {fFile && <span className="icon-form-file">{fFile.name}</span>}
                  </div>
                </>
              )}
              <div className="icon-form-actions">
                <button type="button" className="btn btn-secondary btn-sm" onClick={resetForm}>{t('common.cancel')}</button>
                <button type="button" className="btn btn-primary btn-sm" disabled={busy || !canSubmit} onClick={submit}>
                  {t('common.save')}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
